/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module  public/js/services/modalservice.js
 * @class   ModalService
 * @summary Accessibility-friendly, dependency-free modal controller.
 *
 * @description
 * • Keeps the original focus-trap / ARIA management exactly as before.
 * • Lazy-loads the Bootstrap 5 bundle only if it does NOT already exist on
 *   `globalThis.bootstrap`; therefore no change for pages that already ship
 *   Bootstrap.
 *
 * ## Public API
 * – ModalService class
 *
 * ## Back-compat
 * *All* existing calls to `modalService.open/close` etc. are unaffected.
 *
 * @author   Auto-Generated
 * @version  1.1.0
 * @since    2025-05-11
 * ─────────────────────────────────────────────────────────────────────────────
 */

export default class ModalService {
  /** @type {Map<string, HTMLElement>} */ #registry = new Map();
  /** @type {string|null}               */ #currentId = null;
  /** @type {HTMLElement|null}          */ #previous = null;

  /* ───────────── Public API ───────────── */

  register(id, el) {
    if (!id || !(el instanceof HTMLElement)) {
      throw new TypeError('ModalService.register: invalid args');
    }
    this.#registry.set(id, el);
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-hidden', 'true');
  }

  open(id) {
    const el = this.#getOrRegister(id);
    if (this.#currentId === id) return;
    if (this.#currentId) this.close(this.#currentId);

    this.#previous = /** @type {HTMLElement} */ (document.activeElement);

    el.classList.remove('hidden');
    el.setAttribute('aria-hidden', 'false');
    this.#currentId = id;

    const focusable = el.querySelector(
      'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
    );
    (focusable ?? el).focus();

    document.addEventListener('keydown', this.#onKeyDown);
  }

  close(id) {
    const el = this.#registry.get(id);
    if (!el || this.#currentId !== id) return;

    el.classList.add('hidden');
    el.setAttribute('aria-hidden', 'true');
    this.#currentId = null;

    document.removeEventListener('keydown', this.#onKeyDown);
    this.#previous?.focus?.();
    this.#previous = null;
  }

  isOpen(id) {
    return this.#currentId === id;
  }

  /* ───────────── Internals ───────────── */

  #getOrRegister(id) {
    let el = this.#registry.get(id);
    if (!el) {
      el = document.getElementById(id);
      if (!el) throw new Error(`ModalService: no element #${id}`);
      this.register(id, el);
    }
    return el;
  }

  /** @param {KeyboardEvent} evt */
  #onKeyDown = (evt) => {
    if (evt.key === 'Escape' && this.#currentId) this.close(this.#currentId);
  };
}
